import type { StyleSpecification, SourceSpecification } from 'maplibre-gl'
import { tileCacheService } from './tile-cache-service.js'
import { cairnifyTileUrl, cairnifyGlyphUrl } from './cairn-protocol.js'
import {
  OFM_TILEJSON_URL,
  OFM_PINNED_TILE_URL,
  OFM_MINZOOM,
  OFM_MAXZOOM,
} from '../../lib/ofm-config.js'

const OFM_GLYPHS_TEMPLATE = 'https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf'

export const CACHED_GLYPHS = cairnifyGlyphUrl(OFM_GLYPHS_TEMPLATE)

interface TileJSON {
  tiles: string[]
  minzoom?: number
  maxzoom?: number
  [key: string]: unknown
}

class VectorStyleCacheService {
  /**
   * Load a vector style with offline caching and URL rewriting.
   *
   * 1. Fetch/cache the style JSON (SWR)
   * 2. Resolve TileJSON references → inline tile URLs through cairn://
   * 3. Rewrite glyphs → cairn://glyph/...
   * 4. Pre-warm sprite cache (Workbox handles serving)
   */
  async loadStyle(styleUrl: string): Promise<StyleSpecification> {
    const styleJson = await this.fetchStyleJson(styleUrl)
    return this.rewriteStyle(structuredClone(styleJson))
  }

  /**
   * Pin all metadata resources for a style (style JSON, TileJSON, sprites).
   * Called by the plan download workflow.
   */
  async pinStyleResources(styleUrl: string, signal?: AbortSignal): Promise<void> {
    if (signal?.aborted) return

    await tileCacheService.pinResource(`style-json/${styleUrl}`, styleUrl)

    const blob = await tileCacheService.getResource(`style-json/${styleUrl}`, styleUrl)
    if (!blob) return
    const style: StyleSpecification = JSON.parse(await blob.text())

    for (const [, source] of Object.entries(style.sources ?? {})) {
      const src = source as Record<string, unknown>
      if (src.type === 'vector' && typeof src.url === 'string') {
        if (signal?.aborted) return
        await tileCacheService.pinResource(`tilejson/${src.url}`, src.url)
      }
    }

    if (style.sprite) {
      const base = typeof style.sprite === 'string' ? style.sprite : undefined
      if (base) {
        const suffixes = ['.json', '.png', '@2x.json', '@2x.png']
        for (const suffix of suffixes) {
          if (signal?.aborted) return
          const url = base + suffix
          await tileCacheService.pinResource(`sprite/${url}`, url)
        }
      }
    }
  }

  /**
   * Get the resolved tile URL template for a vector source's TileJSON.
   * Useful for plan downloads that need to enumerate tiles.
   */
  async resolveTileTemplate(tileJsonUrl: string): Promise<string | null> {
    const tj = await this.fetchTileJson(tileJsonUrl)
    return tj?.tiles[0] ?? null
  }

  /** Get a cached TileJSON, resolving and caching it if needed. */
  async getTileJson(tileJsonUrl: string): Promise<TileJSON | null> {
    return this.fetchTileJson(tileJsonUrl)
  }

  private async fetchStyleJson(styleUrl: string): Promise<StyleSpecification> {
    const cacheKey = `style-json/${styleUrl}`
    const blob = await tileCacheService.getResource(cacheKey, styleUrl)
    if (blob) return JSON.parse(await blob.text())
    throw new Error(`Failed to load style: ${styleUrl}`)
  }

  private async fetchTileJson(url: string): Promise<TileJSON | null> {
    const cacheKey = `tilejson/${url}`
    const blob = await tileCacheService.getResource(cacheKey, url)
    if (!blob) return null
    return JSON.parse(await blob.text())
  }

  private async rewriteStyle(style: StyleSpecification): Promise<StyleSpecification> {
    await this.rewriteSources(style)

    if (style.glyphs) {
      style.glyphs = cairnifyGlyphUrl(style.glyphs)
    }

    this.prewarmSprites(style)

    return style
  }

  private async rewriteSources(style: StyleSpecification): Promise<void> {
    const sources = style.sources ?? {}

    for (const [name, source] of Object.entries(sources)) {
      const src = source as Record<string, unknown>

      if (src.type === 'vector' && typeof src.url === 'string') {
        const isPinned = OFM_PINNED_TILE_URL && src.url === OFM_TILEJSON_URL
        const tj = isPinned ? null : await this.fetchTileJson(src.url)
        const tileUrl = tj?.tiles[0] ?? OFM_PINNED_TILE_URL ?? src.url
        const rewritten: SourceSpecification = {
          type: 'vector',
          tiles: [cairnifyTileUrl(`ofm-vector-${name}`, tileUrl)],
          minzoom: tj?.minzoom ?? OFM_MINZOOM,
          maxzoom: tj?.maxzoom ?? OFM_MAXZOOM,
        }
        sources[name] = rewritten
      } else if (src.type === 'raster' && Array.isArray(src.tiles)) {
        const tiles = src.tiles as string[]
        if (tiles.length > 0) {
          const rewritten: SourceSpecification = {
            ...source,
            type: 'raster',
            tiles: [cairnifyTileUrl(`ofm-raster-${name}`, tiles[0])],
          } as SourceSpecification
          sources[name] = rewritten
        }
      }
    }
  }

  private prewarmSprites(style: StyleSpecification): void {
    if (!style.sprite) return
    const base = typeof style.sprite === 'string' ? style.sprite : undefined
    if (!base) return

    const suffixes = ['.json', '.png', '@2x.json', '@2x.png']
    for (const suffix of suffixes) {
      const url = base + suffix
      void tileCacheService.getResource(`sprite/${url}`, url)
    }
  }
}

export const vectorStyleCacheService = new VectorStyleCacheService()
